import { useLang } from 'hooks/lang';
import { useMemo, useState } from 'react';

import { Switch, Table, Button, Column, Row, Card } from 'components/Blocks';
import { RefreshIcon } from 'components/Icons';

import { useToast } from 'providers/Toast';
import { actions, loaders, Client } from 'entities';

import Alphabet from './Alphabet';
import ClientOptions from './Options';
import { ClientOptionsProvider } from './OptionsContext';

const firstCharRegex = {
  // this does not match 0-9
  special: /^\W/,
  number: /^\d/,
};

function isWithinLetter({ name }: Client, letter: string) {
  if (letter === '#') return Object.values(firstCharRegex).some((r) => r.test(name));

  return name.toLocaleLowerCase().startsWith(letter);
}

function Clients() {
  const [letter, setLetter] = useState('a');

  const { data, errorOrLoading, load } = loaders.clients.list.use({
    defaultValue: [],
  });

  const { addToast } = useToast();

  const { t } = useLang();

  const syncSample = actions.clients.syncSample.use({
    onSuccess: ({ synced }) => {
      addToast({
        title: `Synced ${synced} new clients`,
      });

      load();
    },
  });

  const clients = useMemo(
    () => data.filter((client) => isWithinLetter(client, letter)),
    [data, letter],
  );

  const loading = syncSample.loading || errorOrLoading;

  return (
    <ClientOptionsProvider>
      <Column>
        <Row justifyContent="space-between">
          <h4 className="font-medium">{t('clients')}</h4>

          <Button disabled={loading} onClick={syncSample.execute} variant="ghost">
            <RefreshIcon />
            {t('sync-sample')}
          </Button>
        </Row>

        <Alphabet letter={letter} setLetter={setLetter} />

        <Card>
          <Table
            loading={loading}
            items={clients}
            uniqueKey="id"
            itemsPerPage={15}
            columns={[
              { label: t('name') as string, property: 'name', search: true },
              { label: 'Sample ID' as string, property: 'id' },
              {
                render: (client) => (
                  <Row justifyContent="flex-end" fullWidth={false} gap="2">
                    <ClientOptions client={client} />

                    <Switch disabled checked={client.active} size="small" />
                  </Row>
                ),
              },
            ]}
          />
        </Card>
      </Column>
    </ClientOptionsProvider>
  );
}

export default Clients;
